import { useToast } from '@scality/core-ui';
import { useRef } from 'react';
import { userManager } from '../Auth';

const POPUP_NAME = 'guardian-login-popup';

function toFriendlyError(e: unknown): string {
  const err = e as Record<string, unknown>;
  if (typeof err?.error_description === 'string') return err.error_description;
  const code = typeof err?.error === 'string' ? err.error : '';
  switch (code) {
    case 'access_denied': return 'Access denied. You may not have permission to access Guardian.';
    case 'login_required': return 'Your session has expired. Please log in again.';
    case 'server_error':   return 'The authentication server encountered an error. Please try again later.';
    default: return e instanceof Error && e.message ? e.message : 'Authentication failed.';
  }
}

export function useAuthPopup() {
  const toast = useToast();
  const abortControllerRef = useRef<AbortController | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const popupRef = useRef<Window | null>(null);

  const showError = (message: string) => {
    toast.showToast({ status: 'error', autoDismiss: true, open: true, message });
  };

  const cleanup = () => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const login = async () => {
    // Cancel any in-flight attempt before starting a new one.
    cleanup();
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    abortControllerRef.current?.abort('Restarted by user');

    // Pre-open the popup ourselves so we hold the reference for reliable closure detection.
    // The library reuses the named window (window.open(void 0, POPUP_NAME, ...)) without
    // navigating it, then navigates it via location.replace() — this works cross-origin.
    const popup = window.open('', POPUP_NAME, 'width=520,height=620');
    if (!popup) {
      showError('Popups are blocked. Please allow popups for this site and try again.');
      return;
    }
    popupRef.current = popup;

    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Poll popup.closed ourselves — more reliable than the library's internal polling
    // when running inside an iframe (the library's setInterval silently fails on cross-origin
    // popup references in some iframe security contexts).
    intervalRef.current = setInterval(() => {
      if (popup.closed) {
        cleanup();
        if (!controller.signal.aborted) {
          controller.abort('Popup closed by user');
        }
      }
    }, 500);

    try {
      await userManager.signinPopup({
        popupWindowTarget: POPUP_NAME,
        popupSignal: controller.signal,
      });
    } catch (e: unknown) {
      if (controller.signal.aborted) {
        showError('Login was cancelled. Please try again.');
      } else {
        showError(toFriendlyError(e));
      }
    } finally {
      cleanup();
    }
  };

  return { login };
}
